import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const QuoteModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [deedPhoto, setDeedPhoto] = useState(null);
  const [deedPhotoPreview, setDeedPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      setDeedPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeedPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setDeedPhoto(null);
    setDeedPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validasyon
    if (!formData.full_name || !formData.phone || !formData.email) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    if (!deedPhoto) {
      toast.error('Tapu fotoğrafı yüklemeniz zorunludur');
      return;
    }

    setIsSubmitting(true);

    try {
      // Direkt mailto ile e-posta istemcisini aç
      const emailBody = `
Yeni Teklif Talebi

Ad Soyad: ${formData.full_name}
Telefon: ${formData.phone}
Email: ${formData.email}
Mesaj: ${formData.message}

NOT: Tapu fotoğrafınızı lütfen bu e-postaya manuel olarak ekleyin.
      `;
      
      const mailtoLink = `mailto:migayapi@gmail.com?subject=Teklif Talebi - ${formData.full_name}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      
      toast.success('E-posta istemciniz açılıyor. Lütfen tapu fotoğrafınızı manuel olarak ekleyin ve gönderin.');
      
      // Form'u temizle
      setTimeout(() => {
        setFormData({ full_name: '', phone: '', email: '', message: '' });
        setDeedPhoto(null);
        setDeedPhotoPreview(null);
        onOpenChange(false);
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-modal" className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-font text-2xl text-[#1e4a7a]">Teklif Alın</DialogTitle>
          <DialogDescription>
            Tapu belgenizi yükleyerek ücretsiz teklif alabilirsiniz.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="full_name">Ad Soyad *</Label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              data-testid="quote-full-name-input"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phone">Telefon *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              data-testid="quote-phone-input"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              data-testid="quote-email-input"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="message">Mesaj</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              data-testid="quote-message-input"
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="deed_photo">Tapu Fotoğrafı * (Maks 5MB)</Label>
            {!deedPhotoPreview ? (
              <div className="mt-1">
                <label
                  htmlFor="deed_photo"
                  data-testid="deed-photo-upload-label"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#dc2626] transition-colors"
                >
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Tapu fotoğrafını yüklemek için tıklayın</span>
                  <input
                    id="deed_photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    data-testid="deed-photo-input"
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="mt-1 relative">
                <img
                  src={deedPhotoPreview}
                  alt="Tapu önizleme"
                  data-testid="deed-photo-preview"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removePhoto}
                  data-testid="remove-deed-photo-button"
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="submit-quote-button"
            className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-2 rounded-full font-medium transition-all duration-300"
          >
            {isSubmitting ? 'Gönderiliyor...' : 'Teklif Talebi Gönder'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;